import React, { useRef, useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';

import ic1 from '../../../assets/assets-homepage/img/icons/ic1.svg';
import icFootprint from '../../../assets/assets-safari/icons/footprint.svg';
import ic3 from '../../../assets/assets-homepage/img/icons/ic4.svg';
import './WhyWorkWithXploriusSafari.scss';

type Card = {
  icon: string;
  key: string;
  style?: string;
};

const cards: Card[] = [
  { icon: ic1, key: 'planning' },
  { icon: icFootprint, key: 'expertise', style: 'w-full sm:w-[65.5px]' },
  { icon: ic3, key: 'concierge' },
];

const CardItem: React.FC<{ card: Card; iconStyle?: string }> = ({ card, iconStyle }) => {
  const { t } = useTranslation('safari');
  return (
    <div className="why-work-safari-card-wrapper">
      <div className="why-work-safari-card !items-start !py-3 !px-3 sm:!py-6 sm:!px-6 h-full">
        <div className={`bg-[#EFE8DF] rounded-[6px] sm:rounded-xl w-[34px] h-[32px] sm:w-[69px] sm:h-[65px] flex items-center justify-center mb-4 sm:mb-8 lg:mb-6`}>
          <img src={card.icon} alt="" className={`w-5 sm:w-8 ${iconStyle}`} />
        </div>

        <div>
          <h3 className="font-medium text-sm sm:text-[22px] text-black mb-[5.5px] sm:mb-2 text-left leading-tight">
            {t(`whyWork.cards.${card.key}.title`)}
          </h3>

          <p className="text-xs sm:text-lg text-[#4A4A4A] leading-relaxed text-left">
            {t(`whyWork.cards.${card.key}.description`)}
          </p>
        </div>
      </div>
    </div>
  );
};

const WhyWorkWithXploriusSafari: React.FC = () => {
  const { t } = useTranslation('safari');
  const swiperInstanceRef = useRef<SwiperType | null>(null);
  const sliderContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1099px)');

    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    handleMediaChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!swiperInstanceRef.current) return;
      const swiper = swiperInstanceRef.current;
      const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide');

      if (!clickedSlide) return;

      if (clickedSlide.classList.contains('swiper-slide-next')) {
        swiper.slideNext();
      } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
        swiper.slidePrev();
      }
    };

    const sliderElement = sliderContainerRef.current;
    if (sliderElement) {
      sliderElement.addEventListener('click', handleClick);
    }

    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener('click', handleClick);
      }
    };
  }, [isMobile]);

  return (
    <section className="bg-[#9B2626] lg:py-[60px] py-10 overflow-hidden lg:!my-[60px] sm:!my-10 !my-8">
      <div className="max-w-[1600px] mx-auto lg:px-10">
        <h2 className="section-heading !text-white uppercase mb-6 sm:mb-8 lg:mb-10 text-center">
          <Trans
            i18nKey={`whyWork.heading`}
            ns="safari"
            components={{ br1: <br className='sm:hidden' /> }}
          />
        </h2>

        {isMobile ? (
          <div ref={sliderContainerRef}>
            <Swiper
              onSwiper={(swiper: SwiperType) => { swiperInstanceRef.current = swiper; }}
              modules={[Pagination]}
              slidesPerView="auto"
              spaceBetween={32}
              rewind={true}
              pagination={{
                clickable: true,
                el: '.why-work-safari-pagination'
              }}
              className="px-5 sm:px-10 lg:px-0"
              breakpoints={{
                640: {
                  spaceBetween: 40
                }
              }}
            >
              {cards.map((card, index) => (
                <SwiperSlide key={index} className="w-[270px] sm:w-[472px]">
                  <CardItem card={card} iconStyle={card.style} />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="pagination why-work-safari-pagination mt-8" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-[83px] gap-y-6">
            {cards.map((card, index) => (
              <CardItem key={index} card={card} iconStyle={card.style} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyWorkWithXploriusSafari;
